/* istanbul ignore file */
import { CountryCode } from 'libphonenumber-js';
import { TRegion } from '@typescript/index';
import getPublicEnv from '@utils/getPublicEnv';
import { parseSentryIgnoreErrors, SENTRY_ENABLED_ENVIROMENTS } from './sentry-init';
export const ROLES = {
  ECOMMERCE_DEVELOPER: 'ecommerce-developer'
};
export const SUPPORTED_OFFERS = {
  pl: 'VOX-PL-ECOMMERCE',
  mx: 'VOX-MX-ECOMMERCE',
  global: 'VOX-GLOBAL-ECOMMERCE',
  eur: 'VOX-EUR-ECOMMERCE'
};
export const SUPPORTED_PRICELISTS = {
  pl: 'VOX-PL-DETAL',
  mx: 'VOX-MX-DETAL',
  global: 'VOX-GLOBAL',
  eur: 'VOX-EUR-DETAL'
};
export const SUPPORTED_STORAGES = {
  pl: 'VOX-PL-ECOMMERCE',
  mx: 'VOX-MX-MX99',
  global: 'VOX-GLOBAL',
  eur: 'VOX-PL-ECOMMERCE'
};
export const PIMCORE_LOCALE_FALLBACK = {
  'en-EN': 'en',
  'pl-PL': 'pl',
  'es-ES': 'es',
  pl: 'pl',
  en: 'en',
  'pl-MX': 'pl',
  'ro-RO': 'ro',
  'bg-BG': 'bg',
  ro: 'ro',
  bg: 'bg',
  de: 'de',
  fr: 'fr'
};
export const FALLBACK_ISO_LOCALES = {
  pl: 'pl-PL',
  en: 'en-US'
};
export const OFFER_TO_REGION_MAP = {
  'VOX-PL-ECOMMERCE': 'pl',
  'VOX-MX-ECOMMERCE': 'mx',
  'VOX-GLOBAL-ECOMMERCE': 'global',
  'VOX-EUR-ECOMMERCE': 'eur',
  'VOX-BG-ECOMMERCE': 'bg',
  'VOX-RO-ECOMMERCE': 'ro'
};
export const OFFER_TO_COUNTRY_MAP = {
  'VOX-PL-ECOMMERCE': 'pl',
  'VOX-MX-ECOMMERCE': 'mx',
  'VOX-GLOBAL-ECOMMERCE': 'pl,mx',
  'VOX-BG-ECOMMERCE': 'bg',
  'VOX-RO-ECOMMERCE': 'ro'
};
const isServerSide = typeof window === 'undefined';
export const REST_URI = isServerSide ? process.env.PIMCORE_REST_URI : getPublicEnv('NEXT_PUBLIC_PIMCORE_REST_URI') || '';
export const API_KEY = (isServerSide ? process.env.PIMCORE_API_KEY : 'test') as string;
export const PUBLIC_HOST = (isServerSide ? process.env.PUBLIC_HOST : getPublicEnv('NEXT_PUBLIC_HOST')) as string;
export const INTERNAL_HOST = (isServerSide ? process.env.INTERNAL_HOST || process.env.PUBLIC_HOST : getPublicEnv('NEXT_PUBLIC_INTERNAL_HOST') || getPublicEnv('NEXT_PUBLIC_HOST')) as string;
export const PUBLICATION_FLAG = (isServerSide ? process.env.PUBLICATION_FLAG : getPublicEnv('NEXT_PUBLIC_PUBLICATION_FLAG') || 'prod') as string;
export const REVALIDATION_TIME = parseInt(process.env.REVALIDATION_TIME || '0', 10) || 300;
export const KEYCLOAK_REGISTRATION_URI = `${isServerSide ? process.env.KEYCLOAK_ISSUER : getPublicEnv('NEXT_PUBLIC_KEYCLOAK_ISSUER')}/protocol/openid-connect/registrations`;
export const DEFAULT_URL_PRIVACY_POLICY = (isServerSide ? process.env.URL_PRIVACY_POLICY : getPublicEnv('NEXT_PUBLIC_URL_PRIVACY_POLICY') || '/pages/privacy-policy') as string;
export const EXPERTSENDER_ENABLED = (isServerSide ? process.env.EXPERTSENDER_ENABLED : getPublicEnv('NEXT_PUBLIC_EXPERTSENDER_ENABLED') || 'false') as string;
export const DEFAULT_LOCALE = (isServerSide ? process.env.DEFAULT_LOCALE : getPublicEnv('NEXT_PUBLIC_DEFAULT_LOCALE') || 'en') as string;
export const FILE_CACHE_DEBUG = (isServerSide ? process.env.FILE_CACHE_DEBUG : getPublicEnv('NEXT_PUBLIC_FILE_CACHE_DEBUG') || 'false') as 'true' | 'false';
export const URL_TERMS = (isServerSide ? process.env.URL_TERMS : getPublicEnv('NEXT_PUBLIC_URL_TERMS') || '/pages/terms') as string;

/** Regulamin sklepu z prefiksem locale. Domyślny język zostaje bez prefiksu, jak w routingu Next.js. */
export const getTermsUrl = (locale?: string | null, defaultLocale?: string | null): string => {
  const termsPath = URL_TERMS || '/pages/terms';
  if (/^(?:[a-z]+:)?\/\//i.test(termsPath)) {
    return termsPath;
  }
  const resolvedDefaultLocale = defaultLocale || DEFAULT_LOCALE;
  if (!locale || locale === resolvedDefaultLocale) {
    return termsPath;
  }
  return `/${locale}${termsPath}`;
};
const PROMO_CONSENT_TERMS_FALLBACK_LOCALE = 'en';
const parsePromoConsentTermsUrls = (): Record<string, string> => {
  const raw = (isServerSide ? process.env.URL_PROMO_CONSENT_TERMS : getPublicEnv('NEXT_PUBLIC_URL_PROMO_CONSENT_TERMS')) as string | undefined;
  if (!raw) {
    return {};
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, string>;
    }
  } catch {
    // Backward compatibility: plain absolute URL string
    return {
      [PROMO_CONSENT_TERMS_FALLBACK_LOCALE]: raw
    };
  }
  return {};
};

/** Resolves promo terms URL for locale; falls back to `en` when locale key is missing. */
export const getPromoConsentTermsUrl = (locale?: string | null): string => {
  const urls = parsePromoConsentTermsUrls();
  const localeUrl = locale ? urls[locale] : undefined;
  if (localeUrl) {
    return localeUrl;
  }
  return urls[PROMO_CONSENT_TERMS_FALLBACK_LOCALE] || '#';
};

/**
 * Mapa linków do PDF-ów ustawowej ochrony konsumenta.
 * Format Magento, jak `URL_PROMO_CONSENT_TERMS`:
 * `["pl":"https://...","en":"https://..."]`
 */
const parseMagentoLangUrlMap = (raw: string): Record<string, string> => {
  const trimmed = raw.trim();
  if (!trimmed) {
    return {};
  }
  const tryParse = (value: string): Record<string, string> | null => {
    try {
      const parsed = JSON.parse(value) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, string>;
      }
    } catch {
      return null;
    }
    return null;
  };
  return tryParse(trimmed) ?? tryParse(trimmed.replace(/^\[/, '{').replace(/\]$/, '}')) ?? {};
};
export const getLegalGuaranteeNoticeUrl = (locale?: string | null): string | undefined => {
  const raw = (isServerSide ? process.env.URL_LEGAL_GUARANTEE_NOTICE || process.env.NEXT_PUBLIC_URL_LEGAL_GUARANTEE_NOTICE : getPublicEnv('NEXT_PUBLIC_URL_LEGAL_GUARANTEE_NOTICE')) as string | undefined;
  if (!raw || !locale) {
    return undefined;
  }
  const normalizedLang = locale.replace('_', '-').split('-')[0];
  const url = parseMagentoLangUrlMap(raw)[normalizedLang];
  return url || undefined;
};
export const URL_INSTALMENTS = (isServerSide ? process.env.URL_INSTALMENTS : getPublicEnv('NEXT_PUBLIC_URL_INSTALMENTS') || '/pages/zakupy-na-raty') as string;
export const URL_FILE_LIST = (isServerSide ? process.env.URL_FILE_LIST : getPublicEnv('NEXT_PUBLIC_URL_FILE_LIST') || 'https://product.vox.ltd/categoryFiles/') as string;
export const USE_REDIS = process.env.NODE_ENV === 'production' && process.env.IS_REVIEW !== 'true';
export const REFRESH_REDIS = process.env.REFRESH_REDIS as string || 'false';
let basePath: string;
if (isServerSide) {
  basePath = typeof process.env.BASE_PATH !== 'undefined' ? process.env.BASE_PATH : '';
} else {
  basePath = getPublicEnv('NEXT_PUBLIC_BASE_PATH') && getPublicEnv('NEXT_PUBLIC_BASE_PATH') !== 'undefined' ? getPublicEnv('NEXT_PUBLIC_BASE_PATH') : '';
}
export const IS_SHOP = (isServerSide ? process.env.IS_SHOP : getPublicEnv('NEXT_PUBLIC_IS_SHOP') || 'true') as string;
export const BASE_PATH: string = basePath;
export const OFFER = (isServerSide ? process.env.OFFER : getPublicEnv('NEXT_PUBLIC_OFFER') || '') as string;
export const RICH_CONTENT_URL = (isServerSide ? process.env.RICH_CONTENT_URL : getPublicEnv('NEXT_PUBLIC_RICH_CONTENT_URL') || '') as string;
export const RICH_CONTENT_GCS = (isServerSide ? process.env.RICH_CONTENT_GCS : getPublicEnv('NEXT_PUBLIC_RICH_CONTENT_GCS') || '') as string;
export const CONFIGURATOR_URI = (isServerSide ? process.env.CONFIGURATOR_URI : getPublicEnv('NEXT_PUBLIC_CONFIGURATOR_URI') || 'https://product.vox.ltd') as string;
const CONFIGURATOR_CREATIVE_BASE_URL_FALLBACK = 'https://vox-files.com/configurator-creative/nonprod/Files/';
export const CONFIGURATOR_CREATIVE_BASE_URL = (isServerSide ? process.env.CONFIGURATOR_CREATIVE_BASE_URL || CONFIGURATOR_CREATIVE_BASE_URL_FALLBACK : getPublicEnv('NEXT_PUBLIC_CONFIGURATOR_CREATIVE_BASE_URL') || CONFIGURATOR_CREATIVE_BASE_URL_FALLBACK) as string;
export const KEYCLOAK_AUTH_CALLBACK_URI = '/api/auth/callback/keycloak';
export const CMS_PATH_MAP = {
  staticPages: 'PAGES',
  homePage: 'HOME',
  footer: 'PAGES/FOOTER',
  cartSidebar: 'PAGES/CART/SIDEBAR',
  collections: 'COLLECTIONS',
  sites: 'SITES',
  returns: 'RETURNS'
};
export const PUBLICATION_FLAG_KEY = {
  prod: 'PUBLICATION',
  test: 'PUBLICATION_TEST'
};
export const SHOWROOMS_MAP_DEFAULT_CONFIG = {
  lat: 51.9902866,
  lng: 23.1132131,
  zoom: 4
};
export { SENTRY_ENABLED_ENVIROMENTS };

// Zmienne do sterowania logami Sentry
export const SENTRY_FRONTEND_LOGGING_ENABLED = (isServerSide ? process.env.SENTRY_FRONTEND_LOGGING_ENABLED : getPublicEnv('NEXT_PUBLIC_SENTRY_FRONTEND_LOGGING_ENABLED') || 'false') as string;
export const SENTRY_BACKEND_LOGGING_ENABLED = (isServerSide ? process.env.SENTRY_BACKEND_LOGGING_ENABLED : getPublicEnv('NEXT_PUBLIC_SENTRY_BACKEND_LOGGING_ENABLED') || 'false') as string;

// Konfiguracja ignorowanych błędów Sentry
export const SENTRY_IGNORE_ERRORS_CONFIG = isServerSide ? process.env.SENTRY_IGNORE_ERRORS ?? '' : getPublicEnv('NEXT_PUBLIC_SENTRY_IGNORE_ERRORS') || '';

// Domyślne błędy do ignorowania
export const DEFAULT_SENTRY_IGNORE_ERRORS = [];

/**
 * Funkcja do parsowania listy ignorowanych błędów z konfiguracji
 * Format: "error1,error2,error3" lub "error1|error2|error3"
 */
export const getSentryIgnoreErrors = (): string[] => {
  if (!SENTRY_IGNORE_ERRORS_CONFIG) {
    return DEFAULT_SENTRY_IGNORE_ERRORS;
  }
  return [...DEFAULT_SENTRY_IGNORE_ERRORS, ...parseSentryIgnoreErrors(SENTRY_IGNORE_ERRORS_CONFIG)];
};
export const DEFAULT_PHONENUMBER_COUNTRY_CODES: CountryCode[] = ['PL', 'MX', 'AT', 'BE', 'DE', 'FR', 'RO', 'BG'];
export const PHONENUMBER_COUNTRY_CODES: Record<TRegion, CountryCode[]> = {
  EUR: ['AT', 'BE', 'DE', 'FR'],
  PL: ['PL'],
  MX: ['MX'],
  EN: DEFAULT_PHONENUMBER_COUNTRY_CODES,
  RO: ['RO'],
  BG: ['BG']
};
export const GOOGLE_SITE_NAME = (isServerSide ? process.env.GOOGLE_SITE_NAME : getPublicEnv('NEXT_PUBLIC_GOOGLE_SITE_NAME')) as string;
export const REACT_PROFILING = (isServerSide ? process.env.REACT_PROFILING : getPublicEnv('REACT_PROFILING')) as string;
export const FILES_V2 = (isServerSide ? process.env.FILES_V2 || process.env.NEXT_PUBLIC_FILES_V2 : getPublicEnv('NEXT_PUBLIC_FILES_V2')) as string;